/**
 * API ↔ board-model mapping for the Cost Scheme agenda.
 *
 * `mapResponseToModel` seeds the editable {@link BoardModel} from the program-scheme GET response;
 * `toSaveRequest` serializes it back into the save payload. The board is the source of truth while the
 * agenda is open, so these two functions are the only bridge to the server shape.
 */
import type {
  GetProgramSchemeResponse,
  ProgramAlternativeCostItemDto,
  ProgramSchemePartItem,
  SaveProgramAlternativeCostItemDto,
  SaveProgramMoneyDto,
  SaveProgramSchemePartDto,
  SaveProgramSchemeRequest,
} from '@/api/generated/schemas';
import { PlaceholderCostType, PlaceholderType } from '@/api/generated/schemas';
import { normalizeCurrency, type Currency } from './currency';
import { DEFAULT_START, DEFAULT_TIMED_HOURS, hoursToTime, timeToHours } from './time';
import type { BoardModel, BoardPart, BoardPartKind } from './types';

/** Derive how a part is drawn: accommodation by type, service when it has no start time, else timed. */
function deriveKind(type: PlaceholderType, startTime: string | null): BoardPartKind {
  if (type === PlaceholderType.Accommodation) return 'accommodation';
  if (startTime == null) return 'service';
  return 'timed';
}

/** Alternative cost items are passed through verbatim (never edited on the board) so a save never drops them. */
function toSaveAltCostItems(
  items: ProgramAlternativeCostItemDto[] | null | undefined
): SaveProgramAlternativeCostItemDto[] {
  if (!items) return [];
  return items.map((item) => ({
    id: item.id,
    name: item.name,
    facilityUnitPlanTypeId: item.facilityUnitPlanTypeId,
    cost: {
      pax: item.cost.pax
        ? {
            amount: item.cost.pax.amount,
            currency: item.cost.pax.currency,
            domesticAmount: item.cost.pax.domesticAmount,
          }
        : null,
      group: item.cost.group
        ? {
            amount: item.cost.group.amount,
            currency: item.cost.group.currency,
            domesticAmount: item.cost.group.domesticAmount,
          }
        : null,
    },
  }));
}

/** Response part → board part. */
function mapPart(item: ProgramSchemePartItem, index: number): BoardPart {
  const kind = deriveKind(item.type, item.startTime);
  const startHour = timeToHours(item.startTime);
  const endHour = timeToHours(item.endTime);
  const hours =
    kind === 'timed' && startHour != null && endHour != null && endHour > startHour
      ? endHour - startHour
      : DEFAULT_TIMED_HOURS;
  const span = Math.max(1, item.dayOffsetTo - item.dayOffsetFrom + 1);
  const currency = normalizeCurrency(
    item.expectedCost?.pax?.currency ?? item.expectedCost?.group?.currency
  );

  return {
    uid: item.id,
    serverId: item.id,
    kind,
    type: item.type,
    transportKind: item.transportKind ?? null,
    costType: item.costType,
    name: item.name ?? '',
    showInProgram: item.showInProgram,
    note: item.note ?? null,
    displayOrder: item.displayOrder ?? index,
    day: item.dayOffsetFrom,
    span,
    row: item.displayRow ?? (kind === 'service' ? 0 : null),
    startHour,
    hours,
    costPax: item.expectedCost?.pax?.amount ?? null,
    costGroup: item.expectedCost?.group?.amount ?? null,
    currency,
    facilitySubTypeId: item.facilitySubTypeId ?? null,
    facilitySubTypeName: item.facilitySubTypeName ?? null,
    accommodationCategoryCbId: item.accommodationCategoryCbId ?? null,
    accommodationCategoryName: null,
    unitPatterns: item.unitPatterns ?? [],
    equipmentIds: item.equipmentIds ?? [],
    sourcePlaceholderId: item.sourcePlaceholderId ?? null,
    altCostItems: toSaveAltCostItems(item.alternativeCostItems),
  };
}

/** Seed the editable board model from the program-scheme GET response. */
export function mapResponseToModel(response: GetProgramSchemeResponse): BoardModel {
  return {
    name: response.name ?? '',
    lengthDays: response.lengthDays,
    parts: response.parts.map(mapPart),
  };
}

function toSaveMoney(amount: number | null, currency: Currency): SaveProgramMoneyDto | null {
  if (amount == null || Number.isNaN(amount)) return null;
  // domesticAmount přepočítává autoritativně server dle fixovaného kurzovního lístku série; klientská
  // hodnota se ignoruje, posíláme proto původní částku jako neutrální placeholder.
  return { amount, currency, domesticAmount: amount };
}

/** Board part → save DTO. `displayOrder` is re-derived from array position on every save. */
function toSavePart(part: BoardPart, index: number): SaveProgramSchemePartDto {
  const isTimed = part.kind === 'timed';
  const dayOffsetFrom = part.day;
  const dayOffsetTo = isTimed ? part.day : part.day + part.span - 1;
  const startHour = part.startHour ?? DEFAULT_START;
  const hasCost = part.costType !== PlaceholderCostType.None;

  return {
    id: part.serverId,
    type: part.type,
    transportKind: part.transportKind,
    costType: part.costType,
    name: part.name,
    showInProgram: part.showInProgram,
    note: part.note,
    dayOffsetFrom,
    dayOffsetTo,
    startTime: isTimed ? hoursToTime(startHour) : null,
    endTime: isTimed ? hoursToTime(startHour + part.hours) : null,
    displayOrder: index,
    displayRow: part.kind === 'service' ? (part.row ?? 0) : null,
    sourcePlaceholderId: part.sourcePlaceholderId,
    facilitySubTypeId: part.facilitySubTypeId,
    accommodationCategoryCbId: part.accommodationCategoryCbId,
    unitPatterns: part.unitPatterns,
    equipmentIds: part.equipmentIds,
    expectedCost: hasCost
      ? {
          pax: toSaveMoney(part.costPax, part.currency),
          group: toSaveMoney(part.costGroup, part.currency),
        }
      : null,
    alternativeCostItems: part.altCostItems,
  };
}

/** Serialize the whole board model into the save request. */
export function toSaveRequest(model: BoardModel): SaveProgramSchemeRequest {
  return {
    name: model.name,
    lengthDays: model.lengthDays,
    parts: model.parts.map(toSavePart),
  };
}
